import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usePaginatedQuery } from './UsePaginatedQuery'
import { useOptimisticDelete } from './UseOptimisticMutation'
import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useMemo } from 'react'
import type { Playbook } from '../db/Types'
import {
	createPlaybook as apiCreatePlaybook,
	deletePlaybook as apiDeletePlaybook,
	emptyTrash as apiEmptyTrash,
	fetchPlaybooksPage,
	permanentDeletePlaybook as apiPermanentDeletePlaybook,
	playbookKeys,
	type PlaybookWithCount,
	restorePlaybook as apiRestorePlaybook,
	togglePlaybookStar as apiTogglePlaybookStar,
	updatePlaybook as apiUpdatePlaybook,
} from '../api/queries/PlaybookQueries'

interface UsePlaybooksDataReturn {
	playbooks: PlaybookWithCount[]
	personalPlaybooks: PlaybookWithCount[]
	teamPlaybooks: PlaybookWithCount[]
	isLoading: boolean
	error: string | null
	fetchNextPage: () => void
	hasNextPage: boolean
	isFetchingNextPage: boolean
	createPlaybook: (name: string, teamId: string | null, description?: string) => Promise<Playbook>
	updatePlaybook: (id: string, updates: { name?: string; description?: string }) => Promise<void>
	deletePlaybook: (id: string) => Promise<void>
	toggleStar: (id: string) => Promise<void>
	restore: (id: string) => Promise<void>
	permanentDelete: (id: string) => Promise<void>
	emptyTrash: () => Promise<number>
	refetch: () => Promise<void>
}

export function usePlaybooksData(
	currentTeamId: string | null = null,
	section: string = 'all',
	folderId: string | null = null,
): UsePlaybooksDataReturn {
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const handleUnauthorized = useCallback((error: Error) => {
		if (error.message === 'UNAUTHORIZED') {
			navigate('/login')
		}
	}, [navigate])

	// Paginated query with manual state management
	const {
		items: playbooks,
		isLoading,
		error: queryError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = usePaginatedQuery<PlaybookWithCount>({
		queryKey: playbookKeys.list(currentTeamId, section, folderId),
		fetchPage: async (cursor) => {
			const result = await fetchPlaybooksPage(20, cursor, section, folderId, currentTeamId)
			return {
				items: result.playbooks,
				nextCursor: result.nextCursor ?? undefined,
				hasMore: result.hasMore,
			}
		},
		getItemId: (playbook) => playbook.id,
	})

	// Handle unauthorized errors
	useEffect(() => {
		if (queryError) {
			handleUnauthorized(queryError as Error)
		}
	}, [queryError, handleUnauthorized])

	// Derived state - split into personal and team playbooks
	const { personalPlaybooks, teamPlaybooks } = useMemo(() => ({
		personalPlaybooks: playbooks.filter(pb => pb.team_id === null),
		teamPlaybooks: currentTeamId
			? playbooks.filter(pb => pb.team_id === currentTeamId)
			: playbooks.filter(pb => pb.team_id !== null),
	}), [playbooks, currentTeamId])

	// CREATE MUTATION
	const createMutation = useMutation({
		mutationFn: (data: { name: string; team_id: string | null; description?: string }) =>
			apiCreatePlaybook(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: playbookKeys.lists() })
		},
		onError: handleUnauthorized,
	})

	// UPDATE MUTATION
	const updateMutation = useMutation({
		mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string } }) =>
			apiUpdatePlaybook(id, data),
		onError: handleUnauthorized,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: playbookKeys.lists() })
		},
	})

	// DELETE MUTATION (optimistic)
	const deleteMutation = useOptimisticDelete<PlaybookWithCount, string>({
		mutationFn: apiDeletePlaybook,
		queryKey: playbookKeys.list(currentTeamId, section, folderId),
		getId: (playbook) => playbook.id,
		onError: handleUnauthorized,
	})

	// TOGGLE STAR MUTATION
	const toggleStarMutation = useMutation({
		mutationFn: apiTogglePlaybookStar,
		onError: handleUnauthorized,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: playbookKeys.lists() })
		},
	})

	// RESTORE MUTATION
	const restoreMutation = useMutation({
		mutationFn: apiRestorePlaybook,
		onError: handleUnauthorized,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: playbookKeys.lists() })
		},
	})

	// PERMANENT DELETE MUTATION (optimistic)
	const permanentDeleteMutation = useOptimisticDelete<PlaybookWithCount, string>({
		mutationFn: apiPermanentDeletePlaybook,
		queryKey: playbookKeys.list(currentTeamId, section, folderId),
		getId: (playbook) => playbook.id,
		onError: handleUnauthorized,
	})

	// EMPTY TRASH MUTATION
	const emptyTrashMutation = useMutation({
		mutationFn: apiEmptyTrash,
		onError: handleUnauthorized,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: playbookKeys.lists() })
		},
	})

	// WRAPPER FUNCTIONS (maintain API compatibility)
	const createPlaybook = async (
		_name: string,
		_teamId: string | null,
		_description?: string,
	): Promise<Playbook> => {
		return createMutation.mutateAsync({ name: _name, team_id: _teamId, description: _description })
	}

	const updatePlaybook = async (
		id: string,
		updates: { name?: string; description?: string },
	): Promise<void> => {
		await updateMutation.mutateAsync({ id, data: updates })
	}

	const deletePlaybook = async (id: string): Promise<void> => {
		await deleteMutation(id)
	}

	const toggleStar = async (id: string): Promise<void> => {
		await toggleStarMutation.mutateAsync(id)
	}

	const restore = async (id: string): Promise<void> => {
		await restoreMutation.mutateAsync(id)
	}

	const permanentDelete = async (id: string): Promise<void> => {
		await permanentDeleteMutation(id)
	}

	const emptyTrash = async (): Promise<number> => {
		return emptyTrashMutation.mutateAsync()
	}

	const refetch = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: playbookKeys.lists() })
	}, [queryClient])

	return {
		playbooks,
		personalPlaybooks,
		teamPlaybooks,
		isLoading,
		error: queryError?.message || null,
		fetchNextPage,
		hasNextPage: hasNextPage ?? false,
		isFetchingNextPage,
		createPlaybook,
		updatePlaybook,
		deletePlaybook,
		toggleStar,
		restore,
		permanentDelete,
		emptyTrash,
		refetch,
	}
}
